import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Plus, Pencil, Trash2, Eye, Paperclip, ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { EmptyReadings, ErrorState } from '../components/ui/EmptyState';
import AddReadingModal from '../components/readings/AddReadingModal';
import EditReadingModal from '../components/readings/EditReadingModal';
import DeleteReadingModal from '../components/readings/DeleteReadingModal';
import ViewReadingModal from '../components/readings/ViewReadingModal';
import FilePreviewModal from '../components/ui/FilePreviewModal';
import { formatDate } from '../utils/dateUtils';
import { useReadingsByService } from '../hooks/useReadings';
import { useAllServices } from '../hooks/useServices';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const ServiceReadings = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { data: readings = [], isLoading: loading, error, refetch } = useReadingsByService(uid);
    const { data: allServices = [] } = useAllServices();
    const service = allServices.find((s) => s.uid === uid);

    const typeLabels = {
        electricity: t(KEYS.TYPE_ELECTRICITY),
        natural_gas: t(KEYS.TYPE_NATURAL_GAS),
        water: t(KEYS.TYPE_WATER),
        phone: t(KEYS.TYPE_PHONE),
        internet: t(KEYS.TYPE_INTERNET),
        other: t(KEYS.TYPE_OTHER),
    };

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedReading, setSelectedReading] = useState(null);
    const [previewFileUid, setPreviewFileUid] = useState(null);
    const [previewFileLabel, setPreviewFileLabel] = useState('');

    const enrichReading = (reading) => ({
        ...reading,
        serviceUid: uid,
        serviceName: service ? (service.name || typeLabels[service.type] || service.type) : undefined,
        supplierName: service?.supplierName,
        supplierUid: service?.supplierUid,
    });

    const handleView = (reading) => { setSelectedReading(enrichReading(reading)); setIsViewModalOpen(true); };
    const handleEdit = (reading) => { setSelectedReading(enrichReading(reading)); setIsEditModalOpen(true); };
    const handleDelete = (reading) => { setSelectedReading(enrichReading(reading)); setIsDeleteModalOpen(true); };
    const handleFilePreview = (fileUid, label) => { setPreviewFileUid(fileUid); setPreviewFileLabel(label); };

    const serviceTitle = service ? (service.name || typeLabels[service.type] || service.type) : 'Service';

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/services/${uid}`)}
                        className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface-overlay transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-text-primary">{t(KEYS.SERVICE_READINGS_TITLE)}</h1>
                        <p className="text-sm text-text-muted mt-0.5">{serviceTitle}</p>
                    </div>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.SERVICE_READINGS_ADD)}
                </Button>
            </div>

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={5} columns={4} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.SERVICE_READINGS_ERROR_LOAD)} onRetry={refetch} />
            ) : readings.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-right text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_READINGS_COL_VALUE)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">{t(KEYS.SERVICE_READINGS_COL_UNIT)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_READINGS_COL_READ_DATE)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">{t(KEYS.SERVICE_READINGS_COL_NOTES)}</th>
                                    <th className="px-4 py-2.5 w-20"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {readings.map((reading, index) => (
                                    <tr
                                        key={reading.uid}
                                        className="hover:bg-surface-overlay transition-colors group animate-appear"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <td className="px-4 py-2.5 text-sm font-mono font-medium text-right text-text-primary">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <span>{reading.value}</span>
                                                {reading.fileUid && (
                                                    <button
                                                        onClick={() => handleFilePreview(reading.fileUid, `Reading ${reading.value}${reading.unit ? ` ${reading.unit}` : ''}`)}
                                                        className="p-0.5 text-text-muted hover:text-accent rounded transition-colors flex-shrink-0"
                                                        title="View attached file"
                                                    >
                                                        <Paperclip className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-text-muted hidden sm:table-cell">{reading.unit || '—'}</td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted">{formatDate(reading.read_at)}</td>
                                        <td className="px-4 py-2.5 text-xs text-text-muted hidden md:table-cell max-w-xs">
                                            {reading.notes ? <span className="truncate block" title={reading.notes}>{reading.notes}</span> : '—'}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => handleView(reading)} className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors" title="View"><Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></button>
                                                <button onClick={() => handleEdit(reading)} className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors" title="Edit"><Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></button>
                                                <button onClick={() => handleDelete(reading)} className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors" title="Delete"><Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <EmptyReadings onAdd={() => setIsAddModalOpen(true)} />
            )}

            {/* Modals */}
            <AddReadingModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                service={service}
            />
            <EditReadingModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                reading={selectedReading}
            />
            <DeleteReadingModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                reading={selectedReading}
            />
            <ViewReadingModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                reading={selectedReading}
            />
            {previewFileUid && (
                <FilePreviewModal
                    fileUid={previewFileUid}
                    label={previewFileLabel}
                    onClose={() => { setPreviewFileUid(null); setPreviewFileLabel(''); }}
                />
            )}
        </div>
    );
};

export default ServiceReadings;
